import { RecordCollection } from '../../app/record-collection.js';
import { matchesListSearch } from '../../lib/list-search.js';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Combobox,
  FormErrorSummary,
  IconButton,
  Input,
  Modal,
  StatusPill,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Pencil, Plus } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { getApiError, inventoryApi, type InventoryLocation } from '../../lib/api.js';
import {
  ListFilterBar,
  EmptyCard,
  ErrorCard,
  LoadingCard,
  RequestAlert,
} from '../../app/data-view.js';
import type { InventoryCapabilities } from './shared.js';
import { useInventoryUnits } from './inventory-units.js';

const locationSchema = z.object({
  businessUnitId: z.string().optional(),
  displayName: z
    .string()
    .trim()
    .min(2, 'Informe o nome do local.')
    .max(160, 'Use até 160 caracteres.'),
});
type LocationForm = z.infer<typeof locationSchema>;

export function LocationsPanel({ capabilities }: { capabilities: InventoryCapabilities }) {
  const formId = useId();
  const units = useInventoryUnits();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [requestError, setRequestError] = useState<string>();
  const [renaming, setRenaming] = useState<InventoryLocation>();
  const locations = useQuery({
    queryKey: ['company', 'inventory', 'locations'],
    queryFn: () => inventoryApi.listLocations('ALL'),
    retry: false,
  });
  const form = useForm<LocationForm>({
    resolver: zodResolver(locationSchema),
    defaultValues: { displayName: '' },
  });
  const businessUnitId = useWatch({ control: form.control, name: 'businessUnitId' });
  const defaultUnitId =
    units.activeBusinessUnitId ?? (units.options?.length === 1 ? units.options[0]!.value : '');
  useEffect(() => {
    if (open)
      form.reset({
        displayName: '',
        businessUnitId: defaultUnitId,
      });
  }, [form, open, defaultUnitId]);

  function changeOpen(next: boolean) {
    if (!next) setRequestError(undefined);
    setOpen(next);
  }

  async function save(data: LocationForm) {
    setRequestError(undefined);
    if (units.options?.length && !data.businessUnitId) {
      form.setError('businessUnitId', { message: 'Escolha a unidade do local.' });
      return;
    }
    try {
      await inventoryApi.createLocation({
        displayName: data.displayName,
        ...(data.businessUnitId ? { businessUnitId: data.businessUnitId } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: ['company', 'inventory', 'locations'] });
      setOpen(false);
      snackbar.success('Local cadastrado.');
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  const visible = (locations.data ?? []).filter((location) =>
    matchesListSearch(`${location.displayName} ${location.businessUnitName}`, search),
  );
  return (
    <>
      <div className="mb-5 flex justify-end">
        {capabilities.canManageLocations ? (
          <Button onClick={() => setOpen(true)}>
            <Plus className="size-4" />
            Cadastrar local
          </Button>
        ) : null}
      </div>
      <ListFilterBar
        searchLabel="Buscar locais"
        search={search}
        onSearchChange={setSearch}
        count={`${visible.length} ${visible.length === 1 ? 'local' : 'locais'}`}
      />
      {locations.isLoading ? (
        <LoadingCard label="Carregando locais" />
      ) : locations.error ? (
        <ErrorCard message={getApiError(locations.error)} />
      ) : (locations.data?.length ?? 0) === 0 ? (
        <EmptyCard
          icon={MapPin}
          title="Nenhum local cadastrado"
          description="A primeira Entrada de mercadoria cria o local Depósito, e você pode renomeá-lo quando quiser. Se preferir, cadastre antes."
        />
      ) : (
        <RecordCollection
          label="Locais de estoque da empresa"
          records={visible}
          recordKey={(location) => location.id}
          {...(capabilities.canManageLocations
            ? {
                actions: (location: InventoryLocation) => (
                  <IconButton
                    label={`Renomear ${location.displayName}`}
                    icon={<Pencil className="size-4" />}
                    onClick={() => setRenaming(location)}
                  />
                ),
              }
            : {})}
          columns={[
            {
              key: 'name',
              label: 'Local',
              value: (location) => location.displayName,
              render: (location) => <strong>{location.displayName}</strong>,
            },
            {
              key: 'unit',
              label: 'Unidade',
              value: (location) => location.businessUnitName,
              render: (location) => location.businessUnitName,
            },
            {
              key: 'active',
              label: 'Situação',
              value: (location) => Number(location.active),
              render: (location) => (
                <StatusPill tone={location.active ? 'positive' : 'neutral'}>
                  {location.active ? 'Ativo' : 'Arquivado'}
                </StatusPill>
              ),
            },
          ]}
          card={(location) => (
            <article className="rounded-xl border border-surface-border bg-surface-card p-4">
              <strong>{location.displayName}</strong>
              <p className="text-xs text-content-muted">{location.businessUnitName}</p>
              {location.active ? null : <StatusPill tone="neutral">Arquivado</StatusPill>}
              {capabilities.canManageLocations ? (
                <div className="mt-2 flex justify-end">
                  <IconButton
                    label={`Renomear ${location.displayName}`}
                    icon={<Pencil className="size-4" />}
                    onClick={() => setRenaming(location)}
                  />
                </div>
              ) : null}
            </article>
          )}
        />
      )}
      <Modal
        open={open}
        onOpenChange={changeOpen}
        title="Cadastrar local"
        description="Dê um nome livre ao lugar onde os produtos ficam guardados e associe-o a uma unidade. Depósito em outro endereço pode ser uma unidade de apoio."
        footer={
          <>
            <FormErrorSummary form={formId} />
            <Button type="button" variant="outline" onClick={() => changeOpen(false)}>
              Cancelar
            </Button>
            <Button form={formId} loading={form.formState.isSubmitting}>
              Cadastrar local
            </Button>
          </>
        }
      >
        <form
          id={formId}
          className="space-y-4"
          noValidate
          onSubmit={(event) => void form.handleSubmit(save)(event)}
        >
          {units.options?.length ? (
            <Combobox
              label="Unidade do local"
              placeholder="Buscar unidade"
              options={units.options}
              selectedLabel={
                units.options.find((unit) => unit.value === businessUnitId)?.label ?? ''
              }
              clearable={false}
              error={form.formState.errors.businessUnitId?.message}
              onSelect={(choice) => {
                if (choice.kind === 'option')
                  form.setValue('businessUnitId', choice.value, { shouldValidate: true });
              }}
            />
          ) : null}
          <Input
            label="Nome do local"
            error={form.formState.errors.displayName?.message}
            {...form.register('displayName')}
          />
          {requestError ? <RequestAlert message={requestError} /> : null}
        </form>
      </Modal>
      {renaming ? (
        <RenameLocationModal location={renaming} onClose={() => setRenaming(undefined)} />
      ) : null}
    </>
  );
}

const renameSchema = locationSchema.pick({ displayName: true });

/// O nome pode mudar a qualquer momento, inclusive o do local criado na primeira entrada. Saldo e
/// movimentos apontam o local e passam a mostrar o nome novo.
function RenameLocationModal({
  location,
  onClose,
}: {
  location: InventoryLocation;
  onClose: () => void;
}) {
  const formId = useId();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<{ displayName: string }>({
    resolver: zodResolver(renameSchema),
    defaultValues: { displayName: location.displayName },
  });

  async function save(data: { displayName: string }) {
    setRequestError(undefined);
    try {
      await inventoryApi.renameLocation(location.id, {
        displayName: data.displayName,
        businessUnitId: location.businessUnitId,
      });
      await queryClient.invalidateQueries({ queryKey: ['company', 'inventory'] });
      onClose();
      snackbar.success('Local renomeado.');
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Renomear local"
      description="O saldo e os movimentos continuam neste local e passam a mostrar o nome novo."
      footer={
        <>
          <FormErrorSummary form={formId} />
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button form={formId} loading={form.formState.isSubmitting}>
            Salvar nome
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        noValidate
        onSubmit={(event) => void form.handleSubmit(save)(event)}
      >
        <Input
          label="Nome do local"
          error={form.formState.errors.displayName?.message}
          {...form.register('displayName')}
        />
        {requestError ? <RequestAlert message={requestError} /> : null}
      </form>
    </Modal>
  );
}
